import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { CodeAnswer } from './CodeAnswer';

// Active assessment-taking screen: topbar (anti-cheat + timer), progress,
// the current question, the answer input, and navigation.
export function AssessmentQuestion({
  assessment, currentQ, answers, setAnswers, timeLeft, formatTime,
  antiCheatCounts, goToQuestion, onSubmit, showWarning, setShowWarning, warningMsg, runPython,
}) {
  const question = assessment.questions[currentQ];
  const progress = ((currentQ + 1) / assessment.questions.length) * 100;
  const isLastQ = currentQ === assessment.questions.length - 1;
  const isTimeLow = timeLeft < 120;
  const flags = antiCheatCounts.tab_switch + antiCheatCounts.copy_paste;

  const setAnswer = (val) => {
    const next = [...answers];
    next[currentQ] = val;
    setAnswers(next);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F1EAE1', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <div data-testid="assessment-topbar" style={{
        height: 56, borderBottom: '1px solid hsl(var(--border))',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 1.5rem', background: '#fff', position: 'sticky', top: 0, zIndex: 50
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.875rem', fontWeight: 600 }}>{assessment.title}</h3>
          <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Q {currentQ + 1} of {assessment.questions.length}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div data-testid="assessment-anti-cheat-indicator" style={{
            display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.25rem 0.625rem', borderRadius: 999,
            background: flags > 0 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.15)',
            border: `1px solid ${flags > 0 ? 'rgba(245, 158, 11, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`, fontSize: '0.6875rem'
          }}>
            <span className={`status-dot ${flags > 0 ? 'status-processing' : 'status-completed'}`} style={{ width: 6, height: 6 }} />
            <span style={{ color: flags > 0 ? '#D97706' : '#059669' }}>{flags > 0 ? `${flags} flags` : 'Monitored'}</span>
          </div>
          <div data-testid="assessment-timer" style={{
            fontFamily: 'JetBrains Mono', fontSize: '0.9375rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
            color: isTimeLow ? '#EF4444' : '#002A38', padding: '0.375rem 0.75rem', borderRadius: 8,
            background: isTimeLow ? 'rgba(239, 68, 68, 0.12)' : '#fff',
            border: `1px solid ${isTimeLow ? 'rgba(239, 68, 68, 0.3)' : 'hsl(var(--border))'}`,
          }}>
            {formatTime(timeLeft)}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 3, background: 'hsl(var(--border))' }}>
        <div style={{ height: '100%', background: '#0076A3', width: `${progress}%`, transition: 'width 0.3s' }} />
      </div>

      {/* Question area */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '2rem 1.5rem' }}>
        <div style={{ maxWidth: 720, width: '100%' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <Badge style={{
                background: question.type === 'multiple_choice' ? 'rgba(240, 80, 0, 0.15)' : question.type === 'coding' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                color: question.type === 'multiple_choice' ? '#0076A3' : question.type === 'coding' ? '#0076A3' : '#D97706',
                border: `1px solid ${question.type === 'multiple_choice' ? 'rgba(240, 80, 0, 0.3)' : question.type === 'coding' ? 'rgba(124, 58, 237, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                borderRadius: 999, fontSize: '0.6875rem', textTransform: 'capitalize'
              }}>
                {question.type?.replace('_', ' ')}
              </Badge>
              <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{question.skill}</span>
              <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>&middot; {question.points} pts</span>
              <span style={{
                fontSize: '0.6875rem', padding: '0.125rem 0.5rem', borderRadius: 999,
                background: question.difficulty === 'hard' ? 'rgba(239, 68, 68, 0.12)' : question.difficulty === 'medium' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(16, 185, 129, 0.12)',
                color: question.difficulty === 'hard' ? '#DC2626' : question.difficulty === 'medium' ? '#D97706' : '#059669',
              }}>
                {question.difficulty}
              </span>
            </div>
            <h2 data-testid="assessment-question" style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.5 }}>
              {question.question}
            </h2>
          </div>

          <div data-testid="assessment-answer-input" style={{ marginBottom: '2rem' }}>
            {question.type === 'multiple_choice' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {(question.options || []).map((opt) => (
                  <button
                    key={`opt-${opt}`}
                    onClick={() => setAnswer(opt)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', borderRadius: 10, cursor: 'pointer',
                      border: `1px solid ${answers[currentQ] === opt ? '#0076A3' : '#CBD5E1'}`,
                      background: answers[currentQ] === opt ? 'rgba(240, 80, 0, 0.08)' : '#fff',
                      color: '#002A38', fontSize: '0.875rem', textAlign: 'left', width: '100%',
                      transition: 'border-color 0.15s, background 0.15s'
                    }}
                  >
                    <span style={{
                      width: 24, height: 24, borderRadius: '50%',
                      border: `2px solid ${answers[currentQ] === opt ? '#0076A3' : '#CBD5E1'}`,
                      background: answers[currentQ] === opt ? '#0076A3' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {answers[currentQ] === opt && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </span>
                    {opt}
                  </button>
                ))}
              </div>
            ) : question.type === 'coding' ? (
              <CodeAnswer
                value={answers[currentQ] || ''}
                onChange={setAnswer}
                language={question.language}
                runPython={runPython}
              />
            ) : (
              <textarea
                value={answers[currentQ] || ''}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder={'Type your answer here...'}
                rows={6}
                style={{
                  width: '100%', padding: '1rem', borderRadius: 10, border: '1px solid #CBD5E1', background: '#F1EAE1',
                  color: '#002A38', fontSize: '0.875rem', outline: 'none',
                  fontFamily: 'inherit',
                  resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6
                }}
              />
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button onClick={() => goToQuestion(currentQ - 1)} disabled={currentQ === 0} variant="outline" style={{ borderRadius: 10 }}>
              Previous
            </Button>
            <div style={{ display: 'flex', gap: '0.375rem' }}>
              {assessment.questions.map((q, idx) => (
                <button
                  key={q.id || `q-nav-${idx}`}
                  onClick={() => goToQuestion(idx)}
                  style={{
                    width: 10, height: 10, borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0,
                    background: idx === currentQ ? '#0076A3' : answers[idx] ? '#10B981' : '#CBD5E1', transition: 'background 0.15s'
                  }}
                  title={`Question ${idx + 1}`}
                />
              ))}
            </div>
            {isLastQ ? (
              <Button data-testid="assessment-submit-button" onClick={onSubmit} style={{ background: '#10B981', color: '#002A38', borderRadius: 10, fontWeight: 600 }}>
                Submit Assessment
              </Button>
            ) : (
              <Button data-testid="assessment-next-button" onClick={() => goToQuestion(currentQ + 1)} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>
                Next
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Warning Dialog */}
      <Dialog open={showWarning} onOpenChange={setShowWarning}>
        <DialogContent style={{ background: '#fff', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: 14, maxWidth: 400 }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk', color: '#F59E0B' }}>Warning</DialogTitle>
          </DialogHeader>
          <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.6 }}>{warningMsg}</p>
          <DialogFooter>
            <Button onClick={() => setShowWarning(false)} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>I Understand</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
